import { useCallback, useEffect, useState } from 'react';
import {
  avisosPorNegocio,
  indicadoresPlataforma,
  metricasPlataforma,
  type AvisoPorNegocio,
  type Conteo,
  type Indicador,
  type MetricasPlataforma,
  type NivelIndicador,
} from '../../lib/api-panel';
import { resumenDeAviso, useDetalleAviso } from '../../lib/avisos';
import { Esqueleto, FilaTabla, Insignia, MensajeError, TablaAncha, Tarjeta, TextoAyuda } from '../../ui/base';
import { Modal } from '../../ui/Modal';

const REFRESCO_MS = 120_000;
const ALTO_BARRA_REM = 4.5;

/** El día con su mes: para el título del rango y para el texto emergente de cada barra. */
function etiquetaDia(fechaISO: string): string {
  return new Date(`${fechaISO}T12:00:00`).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
}

/**
 * Bajo cada barra va solo el número del día: «20 de ago» mide 55 px y una columna de teléfono tiene
 * 39. El mes va en el título del rango; el conteo en negrita y el día en gris no se leen como fracción.
 */
function numeroDeDia(fechaISO: string): string {
  return String(new Date(`${fechaISO}T12:00:00`).getDate());
}

const TITULOS: Record<string, string> = {
  sonda: 'Sonda del canario',
  jobs: 'Tareas programadas',
  respaldo: 'Respaldo del histórico',
  cuentas_huerfanas: 'Cuentas sin negocio',
  negocios_sin_cuadrar: 'Negocios sin cuadrar',
  existencias_negativas: 'Insumos en negativo',
  integridad: 'Integridad de los datos',
  base_de_datos: 'Base de datos',
  negocios: 'Negocios',
};

/**
 * Qué se rompe si este indicador está en rojo, desde el lado del negocio. No repite el número ni el
 * umbral, y no nombra credenciales ni tablas: cambian sin que nadie actualice el texto.
 */
const IMPACTO: Record<string, string> = {
  sonda: 'Si falla, registrar o leer ventas puede estar caído para todos los negocios.',
  jobs: 'Si una tarea no corre, lo que esa tarea hace deja de pasar: la papelera no se vacía y las vigilancias no revisan nada.',
  respaldo: 'Sin respaldo del día, una pérdida de datos no se puede deshacer más allá del último que haya.',
  cuentas_huerfanas: 'Alguien empezó a registrarse y quedó a medias: su cuenta existe y no puede entrar a ninguna parte.',
  negocios_sin_cuadrar: 'Ese negocio vendió y nadie cerró la caja: el efectivo de esos días queda sin conciliar.',
  existencias_negativas: 'Las recetas o las compras de ese negocio están mal cargadas.',
  integridad: 'Los datos de un negocio dejaron de cuadrar: el cuadre, Hoy o los reportes leen cifras que no existen.',
  base_de_datos: 'Al llegar al límite del plan se detienen las escrituras: nadie podría registrar una venta.',
  negocios: 'Cuántos negocios hay. No cambia de color: es para saber el tamaño de la cartera.',
};

/**
 * En qué grupo va cada indicador: Servicio lo arregla quien opera la plataforma, Operación se
 * resuelve dentro de un negocio. Una clave que no esté aquí se muestra en Servicio.
 */
const SECCIONES = [
  {
    grupo: 'servicio' as const,
    titulo: 'Servicio',
    ayuda:
      'La plataforma en sí: si algo aquí está en rojo, ningún negocio puede hacer nada al respecto. Cada bloque lo abre su proveedor, y lo que va debajo depende de él.',
  },
  {
    grupo: 'operacion' as const,
    titulo: 'Operación',
    ayuda: 'Lo que hay que resolver dentro de los negocios. El desglose por negocio está más abajo.',
  },
];

const GRUPO: Record<string, 'servicio' | 'operacion'> = {
  sonda: 'servicio',
  jobs: 'servicio',
  respaldo: 'servicio',
  base_de_datos: 'servicio',
  cuentas_huerfanas: 'servicio',
  negocios_sin_cuadrar: 'operacion',
  existencias_negativas: 'operacion',
  integridad: 'operacion',
  negocios: 'operacion',
};

/** Lo que está peor va primero: el orden de las tarjetas no debe depender del orden del RPC. */
const PESO: Record<NivelIndicador, number> = {
  critico: 0,
  alerta: 1,
  sin_configurar: 2,
  sin_medir: 2,
  sin_datos: 3,
  ok: 4,
};

const TONO: Record<NivelIndicador, 'exito' | 'aviso' | 'peligro' | 'neutro'> = {
  ok: 'exito',
  alerta: 'aviso',
  critico: 'peligro',
  sin_configurar: 'neutro',
  sin_medir: 'neutro',
  sin_datos: 'neutro',
};

const LEYENDA: Record<NivelIndicador, string> = {
  ok: 'En orden',
  alerta: 'Atención',
  critico: 'Crítico',
  sin_configurar: 'Falta configurar',
  sin_medir: 'Sin medir',
  sin_datos: 'Sin datos',
};

const ETIQUETA_DETALLE: Record<string, string> = {
  minutos: 'Minutos desde la última corrida',
  dias: 'Días desde el último respaldo',
  pct: 'Porcentaje del límite',
  detalle: 'Qué está pasando',
  ultimo: 'Último resultado',
  mb: 'Megabytes usados',
  activos: 'Activos',
  suspendidos: 'Suspendidos',
  prueba: 'De prueba',
};

/** Una fila de la tabla de avisos, con la lista de cuáles son al desplegarla. */
function FilaAviso({ aviso }: { aviso: AvisoPorNegocio }) {
  const { abierto, detalle, error, alternar } = useDetalleAviso(aviso.negocioId, aviso.tipo);

  return (
    <>
      <FilaTabla data-e2e="aviso-negocio">
        <td className="py-1 pr-3 font-semibold">
          <button
            onClick={alternar}
            aria-expanded={abierto}
            className="hover:bg-surface rounded-control -mx-1 px-1 text-left font-semibold"
            data-e2e="aviso-desplegar"
          >
            <span aria-hidden="true" className="text-muted mr-1 inline-block w-3">
              {abierto ? '▾' : '▸'}
            </span>
            {aviso.negocio}
          </button>
        </td>
        <td className="text-muted py-1 pr-3">{aviso.titulo}</td>
        <td className="text-muted py-1 text-right">{resumenDeAviso(aviso)}</td>
      </FilaTabla>
      {abierto && (
        <FilaTabla data-e2e="aviso-detalle">
          <td colSpan={3} className="py-1 pl-5">
            {error !== '' && <MensajeError>{error}</MensajeError>}
            {detalle === null && error === '' && <Esqueleto lineas={1} />}
            {detalle?.map((d) => (
              <p key={d.dato} className="text-etiqueta">
                <span className="num font-bold">{d.dato}</span>
                <span className="text-muted">
                  {' · '}
                  {d.veces === 0
                    ? 'sin avisar hasta hoy'
                    : d.veces === 1
                      ? 'avisado una vez'
                      : `avisado ${d.veces} veces`}
                </span>
              </p>
            ))}
          </td>
        </FilaTabla>
      )}
    </>
  );
}

/**
 * Una tarjeta del Panel. La del proveedor abre su bloque: lleva filo de color y, donde las demás
 * ponen un número, dice qué es ese servicio para el negocio.
 */
function TarjetaIndicador({ indicador: i, onAbrir }: { indicador: Indicador; onAbrir: () => void }) {
  const esServicio = i.paraQue !== undefined;
  const unidad = i.cara?.unidad ?? '';
  return (
    <button className="text-left" onClick={onAbrir} data-e2e="indicador">
      <Tarjeta
        className={`h-full transition-shadow hover:shadow-md ${esServicio ? 'border-l-primary border-l-4' : ''}`}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <span className="font-extrabold">{i.titulo ?? TITULOS[i.clave] ?? i.clave}</span>
          <Insignia tono={TONO[i.estado]}>{LEYENDA[i.estado]}</Insignia>
        </div>
        {esServicio ? (
          <p className="font-bold">{i.paraQue}</p>
        ) : (
          <p className="num text-cifra font-extrabold">
            {i.cara === undefined ? (
              '—'
            ) : (
              <>
                {i.cara.valor}
                {/* El porcentaje va pegado al número; el resto de unidades, separadas. */}
                <span className={`text-muted text-contenido font-bold ${unidad.startsWith('%') ? '' : 'ml-1'}`}>
                  {unidad}
                </span>
              </>
            )}
          </p>
        )}
        {i.lineas ? (
          // El desglose reemplaza al umbral en la tarjeta; el umbral sigue en el diálogo.
          <ul className="mt-2 space-y-1 text-contenido" data-e2e="indicador-lineas">
            {i.lineas.map((l) => (
              <li key={l.texto} className="flex items-baseline justify-between gap-3">
                <span className="text-muted">{l.texto}</span>
                <span className="num font-bold">{l.valor}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted mt-1 text-etiqueta">{i.umbral}</p>
        )}
      </Tarjeta>
    </button>
  );
}

/** La tarjeta del proveedor primero, y detrás lo suyo con lo peor arriba. */
function ordenEnBloque(i: Indicador): number {
  return i.paraQue !== undefined ? -1 : PESO[i.estado];
}

/**
 * Agrupa los indicadores de servicio por proveedor, en el orden de la tabla `servicios`. Los
 * proveedores sin nada debajo comparten fila entre sí, para no dejar la pantalla en blanco.
 */
function bloquesPorServicio(indicadores: Indicador[]): Indicador[][] {
  const porServicio = new Map<string, Indicador[]>();
  for (const i of indicadores) {
    const clave = i.servicio ?? '';
    if (!porServicio.has(clave)) porServicio.set(clave, []);
    porServicio.get(clave)!.push(i);
  }

  const bloques = [...porServicio.values()]
    .map((bloque) => [...bloque].sort((a, b) => ordenEnBloque(a) - ordenEnBloque(b)))
    .sort((a, b) => (a[0]?.orden ?? Infinity) - (b[0]?.orden ?? Infinity));

  const filas: Indicador[][] = [];
  for (const bloque of bloques) {
    const anterior = filas.at(-1);
    if (bloque.length === 1 && anterior?.every((i) => i.paraQue !== undefined)) anterior.push(...bloque);
    else filas.push([...bloque]);
  }
  return filas;
}

export function Indicadores({ verMetricas }: { verMetricas: boolean }) {
  const [indicadores, setIndicadores] = useState<Indicador[] | null>(null);
  const [metricas, setMetricas] = useState<MetricasPlataforma | null>(null);
  const [detalle, setDetalle] = useState<Indicador | null>(null);
  /** Los mismos avisos que ve cada propietario, aquí agrupados por negocio. */
  const [avisos, setAvisos] = useState<AvisoPorNegocio[]>([]);
  const [error, setError] = useState('');

  const cargar = useCallback(() => {
    indicadoresPlataforma()
      .then(setIndicadores)
      .catch((e) => {
        setIndicadores([]);
        setError(e instanceof Error ? e.message : 'No pudimos leer los indicadores.');
      });
    avisosPorNegocio()
      .then(setAvisos)
      .catch(() => setAvisos([]));
    if (verMetricas) metricasPlataforma().then(setMetricas).catch(() => setMetricas(null));
  }, [verMetricas]);

  useEffect(cargar, [cargar]);

  // El refresco se detiene con el detalle abierto: nada se mueve bajo la lectura.
  useEffect(() => {
    if (detalle) return;
    const t = setInterval(cargar, REFRESCO_MS);
    return () => clearInterval(t);
  }, [cargar, detalle]);

  if (indicadores === null) return <Esqueleto lineas={6} />;

  const maximo = Math.max(1, ...(metricas?.tendencia7d ?? []).map((t) => t.ventas));
  const sinOperacionReal = (metricas?.tendencia7d ?? []).every((t) => t.ventas === 0);

  return (
    <div className="space-y-4">
      <MensajeError>{error}</MensajeError>

      {SECCIONES.map(({ grupo, titulo, ayuda }) => {
        const suyos = indicadores.filter((i) => (GRUPO[i.clave] ?? 'servicio') === grupo);
        if (suyos.length === 0) return null;
        const filas =
          grupo === 'servicio'
            ? bloquesPorServicio(suyos)
            : [[...suyos].sort((a, b) => PESO[a.estado] - PESO[b.estado])];
        return (
          <div key={grupo} data-e2e={`grupo-${grupo}`}>
            <h2 className="font-extrabold">{titulo}</h2>
            <TextoAyuda>{ayuda}</TextoAyuda>
            {filas.map((fila) => (
              <div
                key={fila[0]!.clave}
                className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4"
              >
                {fila.map((i) => (
                  <TarjetaIndicador key={i.clave} indicador={i} onAbrir={() => setDetalle(i)} />
                ))}
              </div>
            ))}
          </div>
        );
      })}

      {avisos.length > 0 && (
        <Tarjeta>
          <h2 className="mb-1 font-extrabold">Pendientes de operación, por negocio</h2>
          <TextoAyuda>
            Lo mismo que cada propietario ve en su pantalla Hoy, con los mismos números, y sirve para
            saber a quién escribirle. <strong>Pendientes</strong> es cuántas cosas hay que tocar ahora
            mismo; <strong>avisado</strong>, cuántas veces se ha reportado en siete días. Cada fila se
            abre y dice cuáles son.
          </TextoAyuda>
          <TablaAncha className="mt-3 text-contenido">
            <tbody>
              {avisos.map((a) => (
                <FilaAviso key={`${a.negocioId}-${a.tipo}`} aviso={a} />
              ))}
            </tbody>
          </TablaAncha>
        </Tarjeta>
      )}

      {verMetricas && metricas && (
        <Tarjeta>
          <h2 className="mb-1 font-extrabold">Actividad de la plataforma</h2>
          <TextoAyuda>
            Solo negocios reales. Lo que ocurre en entornos de prueba se muestra aparte porque no es
            operación: son datos sembrados y los que dejan las pruebas automáticas.
          </TextoAyuda>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {[
              ['Comprobantes hoy', metricas.ventasHoy],
              ['Este mes', metricas.ventasMes],
              ['Desde el inicio', metricas.ventasTotal],
              ['Negocios con venta hoy', metricas.negociosConVentaHoy],
              ['Gastos del mes', metricas.gastosMes],
            ].map(([etiqueta, valor]) => {
              const c = valor as Conteo;
              return (
                <div key={String(etiqueta)} className="rounded-control bg-surface px-3 py-2" data-e2e="metrica">
                  <p className="text-muted text-etiqueta font-semibold uppercase tracking-wide">{String(etiqueta)}</p>
                  <p className="num text-cifra font-extrabold">{c.real}</p>
                  {c.prueba > 0 && <p className="text-muted num text-etiqueta">{c.prueba} en entornos de prueba</p>}
                </div>
              );
            })}
          </div>
          <div className="mt-4">
            {/* El rango va en el título porque las barras solo llevan el número del día. */}
            <p className="text-muted mb-2 text-etiqueta font-semibold uppercase tracking-wide">
              Comprobantes por día
              {metricas.tendencia7d.length > 0 &&
                ` · ${etiquetaDia(metricas.tendencia7d[0]!.fecha)} a ${etiquetaDia(
                  metricas.tendencia7d[metricas.tendencia7d.length - 1]!.fecha
                )}`}
            </p>
            {sinOperacionReal ? (
              <p className="text-muted rounded-control bg-surface px-3 py-3 text-contenido">
                Todavía no hay ventas de negocios reales que graficar. Aparecerá aquí en cuanto el
                primero empiece a operar.
              </p>
            ) : (
            /* El alto va en rem y no en porcentaje: un porcentaje contra un padre de alto
               automático no resuelve y todas las barras quedarían del mismo grosor. */
            <div className="flex items-end gap-2">
              {metricas.tendencia7d.map((t) => (
                <div
                  key={t.fecha}
                  className="flex flex-1 flex-col items-center gap-1"
                  title={`${etiquetaDia(t.fecha)}: ${t.ventas} comprobante${t.ventas === 1 ? '' : 's'}`}
                  data-e2e="barra-dia"
                >
                  <span className="num text-etiqueta font-bold">{t.ventas}</span>
                  <div
                    className="w-full rounded-t bg-primary"
                    style={{ height: `${(t.ventas / maximo) * ALTO_BARRA_REM}rem`, minHeight: '3px' }}
                  />
                  <span className="text-muted num text-etiqueta">{numeroDeDia(t.fecha)}</span>
                </div>
              ))}
            </div>
            )}
          </div>
        </Tarjeta>
      )}

      <Modal
        abierto={detalle !== null}
        titulo={detalle ? (detalle.titulo ?? TITULOS[detalle.clave] ?? detalle.clave) : ''}
        onCerrar={() => setDetalle(null)}
      >
        {detalle && (
          <div className="space-y-3">
            <p className="text-contenido">
              {detalle.impacto ?? IMPACTO[detalle.clave] ?? 'Indicador de la plataforma.'}
            </p>
            <p className="text-muted text-contenido">
              <span className="font-semibold">Umbral:</span> {detalle.umbral}
            </p>
            <TablaAncha className="text-contenido">
              <tbody>
                {Object.entries(detalle.detalle).map(([k, v]) => (
                  <FilaTabla key={k}>
                    <td className="text-muted py-1 pr-3">{ETIQUETA_DETALLE[k] ?? k}</td>
                    <td className="num py-1 font-bold">{String(v)}</td>
                  </FilaTabla>
                ))}
              </tbody>
            </TablaAncha>
          </div>
        )}
      </Modal>
    </div>
  );
}
